<template>
  <div ref="container" class="bg-grey-900 flex flex-col max-h-full h-full">
    <!-- Main Loader -->
    <loader 
      v-if="loading"
      dark
      :label="$t('frontend_loading_images')"
      :groove="false"
    />
    
    <div
      v-if="modal"
      class="flex flex-row justify-between"
    >
      <div class="p-4 flex flex-row items-center">
        <close-icon class="w-4 h-4 fill-current" @click="$emit('close-modal')"/>
        <span class="pl-4 text-lg text-white">
          {{ $t('frontend_images_image_library') }}
        </span>
      </div>

      <div class="flex flex-row items-center" v-if="cropping">
        <gd-button class="mr-4" @click="cropping = false" dark>
          {{ $t('frontend_sites_image_picker_goback') }}
        </gd-button>
        <gd-button primary v-if="selectedImage" class="mr-4" size="auto" @click="preConfirmCropping">
          {{ $t('frontend_sites_image_picker_crop_select') }}
        </gd-button>
      </div>

      <div class="flex flex-row items-center" v-else>
        <gd-button class="mr-4" @click="$emit('close-modal')" dark>
          {{ $t('frontend_cancel') }}
        </gd-button>
        <gd-button primary v-if="selectedImage" class="mr-4" @click="emitSelection()">
          {{ $t('frontend_select') }}
        </gd-button>
      </div>
    </div>
    <image-picker-editor ref="cropper" v-model="selectedImage" modal="true" @input="emitSelection()" v-if="cropping" />
    
    <div class="flex flex-row flex-grow" v-else>
      <gd-tabs
        v-if="!loading"
        ref="tabs"
        dark
        :tabs="tabs"
        class="w-full"
        @tab-change="tabChange"
      >
        <template :slot="$t('frontend_images_my_images')">
          <div ref="imageContainer" class="flex flex-row flex-grow">
            <div
              class="flex flex-wrap p-4 overflow-auto"
              :style="{ 'max-height': maxHeight }"
            >
              <v-uploader @add="addImage"/>
              <div
                v-for="image in images.slice(0,imagesPage*imagesPerPage)"
                :key="image.url"
                class="w-48 h-48 rounded m-2 cursor-pointer border relative image-square overflow-hidden"
                :style="getImageStyle(image)"
                :class="{
                  'selected-image border-pink': image.url === selectedImage,
                  'border-grey-600': image.url !== selectedImage,
                }"
                @click="selectImage(image)"
              >
                <!-- Delete Loader -->
                <div class="absolute bg-grey-900 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="deleting === image.url">
                  <loader
                    class="text-white font-semibold text-xs text-center"
                    dark
                    transparent
                    :groove="false"
                    :label="$t('frontend_deleting_image')"
                  />
                </div>
                <ul
                  v-else
                  class="options-list"
                  :class="{'active':openOptions === image.url}"
                >
                  <li class="crop" @click.stop="cropping = true" v-if="allowedToCrop(image.url)"><crop-icon /></li>
                  <li class="delete" @click.stop="deleteImage(image)"><trash-icon /></li>
                  <li class="options" @click.stop="selectOptions(image)">
                    <close-icon  v-if="openOptions === image.url" />
                    <options-icon v-else />
                  </li>
                </ul>
              </div>

              <!-- loader Card -->
              <div class="w-48 h-48 m-2 relative" v-if="loadingImages && this.images.length > (this.imagesPage*this.imagesPerPage)">
                <loader
                  dark
                  transparent
                  class="text-white font-semibold text-xs text-center"
                  :groove="false"
                  :label="$t('frontend_loading_images')"
                />
              </div>

              <!-- infinite for loading more images  -->
              <infinite-loading 
                ref="infiniteLoading"
                v-if="!loading && !loadingImages && this.images.length > (this.imagesPage*this.imagesPerPage)" 
                @infinite="appendImages"
              >
                <div slot="spinner"></div>
              </infinite-loading>
            </div>
          </div>
        </template>
        <template :slot="$t('frontend_images_search_images')">
          <v-search
            :container="$refs.container"
            @uploaded="addUnsplashImage"
          />
        </template>
        <template v-if="gdpEnable" :slot="$t('frontend_groovedesigner_fetch')">
          <gd-tabs
            v-if="!loading"
            ref="gdptabs"
            dark
            :tabs="gdpTabs"
            class="w-full"
            @tab-change="tabChange"
          >
            <div class="flex flex-col bg-grey-900" :slot="$t('frontend_groovedesigner_my_designs')">
              <div class="flex flex-row border-b border-grey-800 p-4">
                <div class="w-80">
                  <text-input
                    v-model="gdpImagesKeyword"
                    :placeholder="$t('frontend_images_search')"
                    @keydown.enter="gdpImagesSearch();"
                    dark
                  />
                </div>
              </div>
              <div ref="gdpImageContainer" class="flex flex-row flex-grow">
                <div
                  v-if="gdpImages && gdpImages.length > 0"
                  class="flex flex-wrap p-4 overflow-auto"
                  :style="{ 'max-height': gdpMaxHeight }"
                >
                  <div
                      v-for="image in gdpImages"
                      :key="image.id"
                      class="w-48 h-48 rounded border m-2 border-grey-600 relative"
                      @mouseout="hovered = null;"
                      @mouseover="hovered = image.id;"
                  >
                    <div
                        class="h-full w-full"
                        :style="getImageStyle(image)"
                        :class="{ 'opacity-50': isActive(image.id) }"
                    ></div>
                    <div
                        class="absolute inset-0 w-full h-full flex items-center justify-center"
                        :class="{ hidden: !isActive(image.id) }"
                        @click="uploadGdImage(image);"
                    >
                        <div
                        class="font-bold mr-3 text-white text-center"
                        v-text="overlayText(image.id)"
                        ></div>
                    </div>
                  </div>

                  <!-- loader Card -->
                  <div class="w-48 h-48 m-2 relative" v-if="loadingGdpImages && !loadedAllGdpImages">
                    <loader
                      dark
                      class="text-white font-semibold text-xs text-center"
                      :label="$t('frontend_loading_images')"
                      :groove="false"
                    />
                  </div>

                  <!-- infinite for loading more images  -->
                  <infinite-loading 
                    ref="gdpInfiniteLoading"
                    :identifier="gdpInfiniteId"
                    v-if="!loadingGdpImages && !loadedAllGdpImages"
                    @infinite="getGrooveDesignerProImages"
                  >
                    <div slot="spinner"></div>
                  </infinite-loading>
                </div>
                
                <div v-else-if="loading || loadingGdpImages" class="text-white m-4">
                    {{ $t('frontend_loading_images') }}
                </div>
                <div v-else class="text-white m-4">
                    {{ $t('frontend_groovedesigner_no_images') }}
                </div>
              </div>
            </div>
            <div class="flex flex-col" :slot="$t('frontend_groovedesigner_my_bundles')">
                <div class="flex flex-row border-b border-grey-800 p-4">
                    <div class="w-80">
                        <text-input
                        v-model="gdpBundlesKeyword"
                        :placeholder="$t('frontend_images_search')"
                        @keydown.enter="gdpBundlesSearch();"
                        dark
                        />
                    </div>
                </div>
                <div ref="gdpBundleContainer" class="flex flex-row flex-grow">
                    <div
                    v-if="gdpBundleImages && gdpBundleImages.length > 0"
                    class="flex flex-wrap p-4 overflow-auto"
                    :style="{ 'max-height': gdpBundleMaxHeight }"
                    >
                        <div
                            v-for="image in gdpBundleImages"
                            :key="image.id"
                            class="w-48 h-48 rounded border m-2 border-grey-600 relative"
                            @mouseout="hovered = null;"
                            @mouseover="hovered = image.id;"
                        >
                        <div
                            class="h-full w-full"
                            :style="getImageStyle(image)"
                            :class="{ 'opacity-50': isActive(image.id) }"
                        ></div>
                        <div
                            class="absolute inset-0 w-full h-full flex items-center justify-center"
                            :class="{ hidden: !isActive(image.id) }"
                            @click="uploadGdImage(image);"
                        >
                            <div
                            class="font-bold mr-3 text-white text-center"
                            v-text="overlayText(image.id)"
                            ></div>
                        </div>
                      </div>

                      <!-- loader Card -->
                      <div class="w-48 h-48 m-2 relative" v-if="loadingGdpBundleImages && !loadedAllGdpBundleImages">
                        <loader
                          dark
                          transparent
                          class="text-white font-semibold text-xs text-center"
                          :groove="false"
                          :label="$t('frontend_loading_images')"
                        />
                      </div>

                      <!-- infinite for loading more images  -->
                      <infinite-loading 
                        ref="gdpBundleInfiniteLoading"
                        :identifier="gdpBundleInfiniteId"
                        v-if="!loadingGdpBundleImages && !loadedAllGdpBundleImages"
                        @infinite="getGrooveDesignerProBundleImages"
                      >
                        <div slot="spinner"></div>
                      </infinite-loading>
                    </div>
                    <div v-else-if="loadingGdpBundleImages || loading" class="text-white m-4">
                        {{ $t('frontend_loading_images') }}
                    </div>
                    <div v-else class="text-white m-4">
                        {{ $t('frontend_groovedesigner_no_bundles') }}
                    </div>
                </div>
            </div>
          </gd-tabs>
        </template>
        <template v-else :slot="$t('frontend_groovedesigner_fetch')">
            <div class="text-white mt-24 mb-8 text-center text-2xl">
                {{ $t('frontend_groovedesigner_connect') }}
            </div>
            <div class="flex flex-row justify-center">
                <gd-button primary size="auto" class="px-24 py-8 text-lg" @click="openUrlInNewTab('https://groovedesignerpro.com/special')">
                  {{ $t('frontend_groovedesigner_signup') }}
                </gd-button>
            </div>
        </template>
        <template v-if="cdEnable" :slot="$t('frontend_clickdesign_fetch')">
            <gd-tabs
            v-if="!loading"
            ref="gdptabs"
            dark
            :tabs="gdpTabs"
            class="w-full"
            @tab-change="tabChange"
            >
                <div class="flex flex-col" :slot="$t('frontend_groovedesigner_my_designs')">
                    <div class="flex flex-row border-b border-grey-800 p-4">
                        <div class="w-80">
                            <text-input
                            v-model="cdImagesKeyword"
                            :placeholder="$t('frontend_images_search')"
                            @keydown.enter="cdImagesSearch();"
                            dark
                            />
                        </div>
                    </div>
                    <div ref="cdImageContainer" class="flex flex-row flex-grow">
                        <div
                        v-if="cdImages && cdImages.length > 0"
                        class="flex flex-wrap p-4 overflow-auto"
                        :style="{ 'max-height': cdMaxHeight }"
                        >
                        <div
                            v-for="image in cdImages"
                            :key="image.id"
                            class="w-48 h-48 rounded border m-2 border-grey-600 relative"
                            @mouseout="hovered = null;"
                            @mouseover="hovered = image.id;"
                        >
                        <div
                            class="h-full w-full"
                            :style="getImageStyle(image)"
                            :class="{ 'opacity-50': isActive(image.id) }"
                        ></div>
                        <div
                            class="absolute inset-0 w-full h-full flex items-center justify-center"
                            :class="{ hidden: !isActive(image.id) }"
                            @click="uploadGdImage(image);"
                        >
                            <div
                            class="font-bold mr-3 text-white text-center"
                            v-text="overlayText(image.id)"
                            ></div>
                        </div>
                      </div>

                      <!-- loader Card -->
                      <div class="w-48 h-48 m-2 relative" v-if="loadingCdImages && !loadedAllCdImages">
                        <loader
                          dark
                          transparent
                          class="text-white font-semibold text-xs text-center"
                          :groove="false"
                          :label="$t('frontend_loading_images')"
                        />
                      </div>

                      <!-- infinite for loading more images  -->
                      <infinite-loading 
                        ref="cdInfiniteLoading"
                        :identifier="cdInfiniteId"
                        v-if="!loadingCdImages && !loadedAllCdImages"
                        @infinite="getClickdesignImages"
                      >
                        <div slot="spinner"></div>
                      </infinite-loading>
                    </div>
                    <div v-else-if="loading || loadingCdImages" class="text-white m-4">
                      {{ $t('frontend_loading_images') }}
                    </div>
                    <div v-else class="text-white m-4">
                      {{ $t('frontend_clickdesigns_no_images') }}
                    </div>
                  </div>
                </div>
                <div class="flex flex-col" :slot="$t('frontend_groovedesigner_my_bundles')">
                    <div class="flex flex-row border-b border-grey-800 p-4">
                        <div class="w-80">
                            <text-input
                            v-model="cdBundleImagesKeyword"
                            :placeholder="$t('frontend_images_search')"
                            @keydown.enter="cdBundleImagesSearch();"
                            dark
                            />
                        </div>
                    </div>
                    <div ref="cdBundleContainer" class="flex flex-row flex-grow">
                        <div
                        v-if="cdBundleImages && cdBundleImages.length > 0"
                        class="flex flex-wrap p-4 overflow-auto"
                        :style="{ 'max-height': cdBundleMaxHeight }"
                        >
                        <div
                            v-for="image in cdBundleImages"
                            :key="image.id"
                            class="w-48 h-48 rounded border m-2 border-grey-600 relative"
                            @mouseout="hovered = null;"
                            @mouseover="hovered = image.id;"
                        >
                        <div
                            class="h-full w-full"
                            :style="getImageStyle(image)"
                            :class="{ 'opacity-50': isActive(image.id) }"
                        ></div>
                        <div
                            class="absolute inset-0 w-full h-full flex items-center justify-center"
                            :class="{ hidden: !isActive(image.id) }"
                            @click="uploadGdImage(image);"
                        >
                            <div
                            class="font-bold mr-3 text-white text-center"
                            v-text="overlayText(image.id)"
                            ></div>
                        </div>
                      </div>

                      <!-- loader Card -->
                      <div class="w-48 h-48 m-2 relative" v-if="loadingCdBundleImages && !loadedAllCdBundleImages">
                        <loader
                          dark
                          transparent
                          class="text-white font-semibold text-xs text-center"
                          :groove="false"
                          :label="$t('frontend_loading_images')"
                        />
                      </div>

                      <!-- infinite for loading more images  -->
                      <infinite-loading 
                        ref="cdBundleInfiniteLoading"
                        :identifier="cdBundleInfiniteId"
                        v-if="!loadingCdBundleImages && !loadedAllCdBundleImages"
                        @infinite="getClickdesignBundles"
                      >
                        <div slot="spinner"></div>
                      </infinite-loading>
                    </div>
                    <div v-else-if="loading || loadingCdBundleImages" class="text-white m-4">
                      {{ $t('frontend_loading_images') }}
                    </div>
                    <div v-else class="text-white m-4">
                      {{ $t('frontend_clickdesigns_no_bundles') }}
                    </div>
                  </div>
              </div>
            </gd-tabs>
        </template>
        <template v-if="youzignEnable"
          :slot="$t('frontend_youzign_fetch')">
          <div ref="imageContainer" class="flex flex-row flex-grow">
            <div
              v-if="youzignImages && youzignImages.length > 0"
              class="flex flex-wrap p-4 overflow-auto"
              :style="{ 'max-height': maxHeight }"
            >
              <div
                v-for="image in youzignImages"
                :key="image.url"
                class="w-48 h-48 rounded border m-2 border-grey-600 relative"
                @mouseout="hovered = null;"
                @mouseover="hovered = image.id;"
              >
              <div
                class="h-full w-full"
                :style="getImageStyle(image)"
                :class="{ 'opacity-50': isActive(image.id) }"
              ></div>
              <div
                class="absolute inset-0 w-full h-full flex items-center justify-center"
                :class="{ hidden: !isActive(image.id) }"
                @click="uploadImage(image);"
              >
                <div
                  class="font-bold mr-3 text-white text-center"
                  v-text="overlayText(image.id)"
                ></div>
              </div>
              </div>
            </div>
            <div v-else class="text-white m-4">
                {{ $t('frontend_youzign_no_images') }}
            </div>
          </div>
        </template>
      </gd-tabs>
    </div>
    <confirm-modal
      ref="confirmSaveModal"
      name="confirm-crop"
      @confirmWithToggleOff="confirmCropping(false)"
      @confirmWithToggleOn="confirmCropping(true)"
      :buttonText="$t('frontend_sites_image_picker_crop_select')"
      :checkboxText="cropConfirmText"
      :confirm-message="$t('frontend_crop_message_title')"
      :messageContent="$t('frontend_crop_message_text')"
      buttonSize="auto"
    />
  </div>
</template>
<style lang="scss">
  .image-square {
    &.selected-image {
      box-shadow: 0 0 11px 1px #fd6481;
      
      ul.options-list {
        bottom: 10px;
        opacity: 1;
      }

      &:before {
        opacity: 1;
      }
    }

    &:before {
      content: "";
      display: block;
      width: 100%;
      height: 50%;
      transition: all ease .3s;
      background: rgb(0,0,0);
      background: linear-gradient(0deg, rgba(0,0,0,0.500437675070028) 0%, rgba(0,0,0,0) 100%);
      position: absolute;
      bottom: 0;
      left: 0;
      opacity: 0;
    }

    &:hover {
      ul.options-list {
        bottom: 10px;
        opacity: 1;
      }

      &:before {
        opacity: 1;
      }
    }

    ul.options-list {
      position: absolute;
      z-index: 2;
      bottom: -20px;
      right: 10px;
      padding-top: 78px;
      opacity: 0;
      transition: all ease .4s;

      > li {
        position: absolute;
        background-color: #fd6481;
        width: 30px;
        height: 30px;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        transition: all ease .2s;

        &:hover {
          background-color: #ea3d5e;
        }

        > svg {
          width: 18px;
          height: 16px;
          * {
            fill: white;
          }
        }
          
        &.crop,&.delete {bottom: 0; opacity:0;}
        &.options {position: relative;}
      }

      &.active {
        > li {
          opacity: 1;
          &.crop {bottom: 80px;}
          &.delete {bottom: 40px;}
          &.options {
            > svg {
              width: 12px;
            }
          }
        }
      }
    }
  }
</style>
<script>
import api from '@groovepages/gd-lib/api';
import auth from '@groovepages/gd-lib/auth';
import InfiniteLoading from 'vue-infinite-loading';
import ConfirmModal from './ConfirmModal';
import cookies from 'js-cookie';
import Loader from './Loader';
import GdTabs from './Tabs';
import GdButton from './Button';
import VResizer from './image-picker/Resizer';
import VUploader from './image-picker/Uploader';
import VSearch from './image-picker/Search';
import {gdImgProxy,isNotAllowedForImgproxy} from '../utils';
import ImagePickerEditor from './ImagePickerEditor';
import CloseIcon from '../assets/close-icon.svg';
import OptionsIcon from '../assets/more-icon-white.svg';
import TrashIcon from '../assets/trash.svg';
import CropIcon from '../assets/crop-icon.svg';

export default {
  components: {
    GdTabs,
    Loader,
    VResizer,
    VUploader,
    VSearch,
    CloseIcon,
    TrashIcon,
    CropIcon,
    OptionsIcon,
    GdButton,
    InfiniteLoading,
    ImagePickerEditor,
    ConfirmModal
  },
  props: ['value', 'containerHeight', 'modal', 'remove'],
  data() {
    return {
      tabs: [
        this.$t('frontend_images_my_images'),
        this.$t('frontend_images_search_images'),
        this.$t('frontend_groovedesigner_fetch'),
      ],
      gdpTabs: [
          this.$t('frontend_groovedesigner_my_designs'),
          this.$t('frontend_groovedesigner_my_bundles'),
      ],
      user: {},
      loading: true,
      images: [],
      imagesPage: 1,
      imagesPerPage: 50,
      loadingImages: false,
      selectedImage: null,
      maxHeight: null,
      photograferObject: {},
      allIntegrations: [],
      youzignEnable: false,
      youzignIntegrationId: null,
      youzignImages: [],
      cdEnable: false,
      cdIntegrationId: null,
      cdMaxHeight: null,
      cdBundleMaxHeight: null,
      cdImages: [],
      cdBundleImages: [],
      loadingCdImages: false,
      loadingCdBundleImages: false,
      loadedAllCdImages: false,
      loadedAllCdBundleImages: false,
      cdImagesPage: 1,
      cdBundleImagesPage: 1,
      cdImagesAuthData: null,
      cdInfiniteId: +new Date(),
      cdBundleInfiniteId: +new Date(),
      gdpEnable: false,
      gdpIntegrationId: null,
      gdpMaxHeight: null,
      gdpBundleMaxHeight: null,
      gdpImages: [],
      gdpBundleImages: [],
      loadingGdpImages: false,
      loadedAllGdpImages: false,
      loadingGdpBundleImages: false,
      loadedAllGdpBundleImages: false,
      gdpImagesPage: 1,
      gdpBundleImagesPage: 1,
      gdpImagesAuthData: null,
      gdpInfiniteId: +new Date(),
      gdpBundleInfiniteId: +new Date(),
      uploading: [],
      hovered: null,
      youzignUser: null,
      cropping: false,
      openOptions: null,
      deleting: null,
      gdpImagesKeyword: '',
      gdpImagesSearchTerm: '',
      gdpBundlesKeyword: '',
      gdpBundlesSearchTerm: '',
      cdImagesKeyword: '',
      cdImagesSearchTerm: '',
      cdBundleImagesKeyword: '',
      cdBundleImagesSearchTerm: '',
    };
  },
  watch: {
    images() {
      this.$nextTick(() => {
        this.setMaxHeight();
      });
    },
    remove() {
      if (this.remove) {
        this.selectedImage = null;
      }
    },
  },
  computed:{
    cropConfirmText(){
      if( cookies.get('crop-message') ) return false;
      else return this.$t('frontend_crop_message_no');
    }
  },
  created() {
    window.addEventListener('resize', this.setMaxHeight);
  },
  destroyed() {
    window.removeEventListener('resize', this.setMaxHeight);
  },
  async mounted() {
    await this.getImages();
    this.user = auth.authenticatedUser();
    this.allIntegrations = this.user.metadata.integrations?JSON.parse(JSON.stringify(this.user.metadata.integrations)):{};
    for(const i in this.allIntegrations)
    {
      if(this.allIntegrations[i].type == "youzign-integration")
      {
        this.youzignEnable = true;
        this.youzignIntegrationId = this.allIntegrations[i].id;
      }

      if(this.allIntegrations[i].type == "clickdesigns-integration")
      {
        this.cdEnable = true;
        this.cdIntegrationId = this.allIntegrations[i].id;
      }

      if(this.allIntegrations[i].type == "groovedesignerpro-integration")
      {
        this.gdpEnable = true;
        this.gdpIntegrationId = this.allIntegrations[i].id;
      }
    }


    if(this.gdpIntegrationId != null)
    {
      this.getGrooveDesignerProCredentials();
    }

    if(this.cdEnable)
    {
      this.tabs.push(this.$t('frontend_clickdesign_fetch'));
    }

    if(this.cdIntegrationId != null)
    {
      this.getClickdesignCredentials();
    }

    if(this.youzignEnable)
    {
      this.tabs.push(this.$t('frontend_youzign_fetch'));
    }

    if(this.youzignIntegrationId != null)
    {
      this.getYouzignCredentials();
    }
  },
  methods: {
    openUrlInNewTab(link) {
      const win = window.open(link, '_blank');
      win.focus();
    },

    allowedToCrop(image) {
      return !isNotAllowedForImgproxy(image);
    },
    setMaxHeight()
    {
        if (!this.images || !this.$refs.imageContainer)
        {
            this.maxHeight = null;
            return;
        }
        const height = window.screen.height - this.$refs.imageContainer.offsetTop - (window.screen.height - this.$refs.container.clientHeight);
        this.maxHeight = `${height}px`;

        if (!this.cdImages || !this.$refs.cdImageContainer)
        {
            this.cdMaxHeight = null;
            return;
        }
        const cdContainerHeight = window.screen.height - this.$refs.cdImageContainer.offsetTop - (window.screen.height - this.$refs.container.clientHeight);
        this.cdMaxHeight = `${cdContainerHeight}px`;


        if (!this.cdBundleImages || !this.$refs.cdBundleContainer)
        {
            this.cdBundleMaxHeight = null;
            return;
        }
        const cdBundleContainerHeight = window.screen.height - this.$refs.cdBundleContainer.offsetTop - (window.screen.height - this.$refs.container.clientHeight);
        this.cdBundleMaxHeight = `${cdBundleContainerHeight}px`;

        if (!this.gdpImages || !this.$refs.gdpImageContainer)
        {
            this.gdpMaxHeight = null;
            return;
        }
        const gdpContainerHeight = window.screen.height - this.$refs.gdpImageContainer.offsetTop - (window.screen.height - this.$refs.container.clientHeight);
        this.gdpMaxHeight = `${gdpContainerHeight}px`;

        if (!this.gdpBundleImages || !this.$refs.gdpBundleContainer)
        {
            this.gdpBundleMaxHeight = null;
            return;
        }
        const gdpBundleContainerHeight = window.screen.height - this.$refs.gdpBundleContainer.offsetTop - (window.screen.height - this.$refs.container.clientHeight);
        this.gdpBundleMaxHeight = `${gdpBundleContainerHeight}px`;
    },

    
    tabChange(tab) {
      if (tab === this.$t('frontend_images_search_images')) {
        this.selectedImage = null;
      }
      this.gdpInfiniteId += 1;
      this.gdpBundleInfiniteId += 1;
      this.cdInfiniteId += 1;
      this.cdBundleInfiniteId += 1;
      this.$nextTick(() => {
        this.setMaxHeight();
      });
    },
    addUnsplashImage(data,from) {
      this.images.unshift(data);
      this.selectedImage = data.url;
      this.$refs.tabs.openTab(this.$t('frontend_images_my_images'));
      /*
      this.photograferObject = {
        first_name: data.first_name,
        last_name: data.last_name,
        photographer_profile_link: data.photographer_profile_link,
        is_from:from == from || 'unsplash',
        from_link:from == undefined ? "https://unsplash.com" : "https://youzign.com"
      };
      */
    },
    addImage(data) {
      this.images.unshift(data);
      this.selectedImage = data.url;
      this.$emit('selected', data);
    },
    deleteSelectedImage() {
      this.images = this.images.filter(
        (image) => image.url !== this.selectedImage
      );
      this.selectedImage = null;
    },
    updateSelectedImage() {
      this.images.forEach((image) => {
        if (image.url !== this.selectedImage) {
          return;
        }
        if (image.url.indexOf('?') === -1) image.url += '?update=1';

        if (image.url.includes('?update')) {
          const split = image.url.split('?update=');
          const revisionNumber = parseInt(split[1], 10) + 1;
          image.url = `${ split[0] }?update=${ revisionNumber }`;
          this.selectedImage = image.url;
        }
      });
    },
    selectImage(image) {
      this.openOptions = null;
      
      if (image.url === this.selectedImage) {
        this.selectedImage = null;
      } else {
        this.selectedImage = null;
        this.$nextTick(() => {
          this.selectedImage = image.url;
        });
        this.photograferObject = null;
      }
    },
    selectOptions(image) {
      if (image.url === this.openOptions) {
        this.openOptions = null;
      } else {
        this.openOptions = null;
        this.$nextTick(() => {
          if( this.selectedImage !== image.url )
            this.selectedImage = image.url;
          this.openOptions = image.url;
        });
      }
    },
    isActive(id) {
      return id === this.hovered || this.uploading.indexOf(id) !== -1;
    },
    overlayText(id) {
      if (this.uploading.indexOf(id) === -1)
        return this.$t('frontend_images_add_to_my_images');
      return this.$t('frontend_uploading');
    },
    async uploadImage(imageData) {
      imageData.user = this.youzignUser;
      this.uploading.push(imageData.id);
      const image = await api.post(`${ window.gdEnv.VUE_APP_API_URL }/groovedigital/images/youzign/save`, { 'imageData': imageData });
      this.addUnsplashImage(image, 'Youzign');
      this.uploading = this.uploading.filter(stored => stored !== imageData.id);
    },
    async uploadGdImage(imageData) {
      this.uploading.push(imageData.id);
      const image = await api.post(`${ window.gdEnv.VUE_APP_API_URL }/groovedigital/images/clickdesigns/save`, { 'imageData': imageData });
      this.addUnsplashImage(image, 'Youzign');
      this.uploading = this.uploading.filter(stored => stored !== imageData.id);
    },
    emitSelection() {
      this.$emit('selected', this.selectedImage);
      this.$emit('close-modal');
    },
    async getImages() {
      try {
        this.images = await api.get(`${ window.gdEnv.VUE_APP_API_URL }/groovedigital/images/user/list`);
        const selected = this.value && this.images.find(({ url }) => url === this.value);
        if (selected) {
          this.selectedImage = selected.value;
        }
      } finally {
        this.loading = false;
      }
    },
    getImageStyle(image) {
      const thumbnailUrl = (this.tab === this.$t('frontend_images_search_images')) ? image.url : gdImgProxy({
        url: image.url,
        width: 210,
        height: 140
      });
      return `background-image: url('${ thumbnailUrl }'); background-repeat: no-repeat; background-size: cover; background-position: 50% 50%;`;
    },
    getYouzignCredentials() {
      api
          .post(`${ window.gdEnv.VUE_APP_API_URL }/groovedigital/plugins/call/youzign/${ this.youzignIntegrationId }`, { 'action': 'get-key' })
          .then((result) => {
            if (result.api_key && result.token) {
              this.getYouzignProfile(result);
              this.getYouzignImages(result);
              this.loading = false;
            } else {
              this.loading = false;
              this.tabs.splice(2, 1);
            }

          })
        .catch((error) => {
          console.error(error);
          this.loading = false;
        });
    },
    getClickdesignCredentials() {
      api
          .post(`${ window.gdEnv.VUE_APP_API_URL }/groovedigital/plugins/call/clickdesigns/${ this.cdIntegrationId }`, { 'action': 'get-key' })
          .then((result) => {
            if (result.api_key) {
              this.cdImagesAuthData = result;
              this.getClickdesignImages();
              this.getClickdesignBundles();
              this.loading = false;
            } else {
              this.loading = false;
              this.tabs.splice(2, 1);
            }

          })
        .catch((error) => {
          console.error(error);
          this.loading = false;
        });
    },
    getGrooveDesignerProCredentials() {
      api
          .post(`${ window.gdEnv.VUE_APP_API_URL }/groovedigital/plugins/call/groovedesignerpro/${ this.gdpIntegrationId }`, { 'action': 'get-key' })
          .then((result) => {
            if (result.api_key) {
              this.gdpImagesAuthData = result;
              this.getGrooveDesignerProImages();
              this.getGrooveDesignerProBundleImages();
              this.loading = false;
            } else {
              this.loading = false;
              this.tabs.splice(2, 1);
            }

          })
        .catch((error) => {
          console.error(error);
          this.loading = false;
        });
    },
    getYouzignProfile(authData){
      api
        .post(`${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/youzign/call/profile`,{"key": authData.api_key, "token" : authData.token})
        .then((result) => {
          this.youzignUser = JSON.parse(result.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getYouzignImages(authData) {
      api
        .post(`${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/youzign/call/designs`,{"key": authData.api_key, "token" : authData.token})
        .then((result) => {
          // console.log(JSON.parse(result));
          const resp = JSON.parse(result.data);
          // console.log('a',resp);
          for(const i in resp)
          {
            this.youzignImages.push({
              'name': resp[i].title,
              'thumbnail_url': resp[i].image_sizes.thumbnail[0],
              'url': resp[i].image_src[0],
              'id': resp[i].id,
              'other': resp[i]
            });
          }
          this.loading = false;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        });
    },

    cdImagesSearch() {
        this.cdImagesPage = 1;
        this.cdImages = [];
        this.cdImagesSearchTerm = this.cdImagesKeyword;
        this.getClickdesignImages();
    },

    getClickdesignImages($state = null) {
      this.loadingCdImages = true;
      api
        .post(`${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/clickdesigns/call/designs`,{"key": this.cdImagesAuthData.api_key, "page": this.cdImagesPage, "search": this.cdImagesSearchTerm})
        .then((result) => {
          const resp = result.images;
          if(resp.length > 0)
          {
            for(const i in resp)
            {
                this.cdImages.push({
                'id': resp[i].id,
                'name': resp[i].title,
                'thumbnail_url': resp[i].thumb,
                'url': resp[i].preview
                });
            }
            if($state)
            {
                $state.loaded();
            }
          }
          else
          {
              this.loadedAllCdImages = true;   
              if($state)
              {
                  $state.complete();
              }
          }
          this.loading = false;
          this.loadingCdImages = false;
          this.cdImagesPage++;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        });
    },


    cdBundleImagesSearch() {
        this.cdBundleImagesPage = 1;
        this.cdBundleImages = [];
        this.cdBundleImagesSearchTerm = this.cdBundleImagesKeyword;
        this.getClickdesignBundles();
    },

    getClickdesignBundles($state = null) {
      this.loadingCdBundleImages = true;
      api
        .post(`${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/clickdesigns/call/bundles`,{"key": this.cdImagesAuthData.api_key, "page": this.cdBundleImagesPage, "search": this.cdBundleImagesSearchTerm})
        .then((result) => {
          const resp = result.images;
          if(resp.length > 0)
          {
            for(const i in resp)
            {
                this.cdBundleImages.push({
                'id': resp[i].id,
                'name': resp[i].title,
                'thumbnail_url': resp[i].thumb,
                'url': resp[i].preview
                });
            }
            if($state)
            {
                $state.loaded();
            }
          }
          else
          {
              this.loadedAllCdBundleImages = true;   
              if($state)
              {
                  $state.complete();
              }
          }
          this.loading = false;
          this.loadingCdBundleImages = false;
          this.cdBundleImagesPage++;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        });
    },

    gdpImagesSearch() {
        this.gdpImagesPage = 1;
        this.gdpImages = [];
        this.gdpImagesSearchTerm = this.gdpImagesKeyword;
        this.getGrooveDesignerProImages();
    },

    getGrooveDesignerProImages($state = null) {
        this.loadingGdpImages = true;
        api
        .post(`${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/clickdesigns/call/designs`,{"key": this.gdpImagesAuthData.api_key, "page": this.gdpImagesPage, "search": this.gdpImagesSearchTerm})
        .then((result) => {
          const resp = result.images;

          if(resp.length > 0)
          {
            for(const i in resp)
            {
                this.gdpImages.push({
                'id': resp[i].id,
                'name': resp[i].title,
                'thumbnail_url': resp[i].thumb,
                'url': resp[i].preview
                });
            }
            if($state)
            {
                $state.loaded();
            }
          }
          else
          {
              this.loadedAllGdpImages = true;   
              if($state)
              {
                  $state.complete();
              }
          }
          this.loading = false;
          this.loadingGdpImages = false;
          this.gdpImagesPage++;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        });
    },

    gdpBundlesSearch() {
        this.gdpBundleImagesPage = 1;
        this.gdpBundleImages = [];
        this.gdpBundlesSearchTerm = this.gdpBundlesKeyword;
        this.getGrooveDesignerProBundleImages();
    },

    getGrooveDesignerProBundleImages($state = null) {
        this.loadingGdpBundleImages = true;
        api
        .post(`${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/clickdesigns/call/bundles`,{"key": this.gdpImagesAuthData.api_key, "page": this.gdpBundleImagesPage, "search": this.gdpBundlesSearchTerm})
        .then((result) => {
          const resp = result.images;

          if(resp.length > 0)
          {
            for(const i in resp)
            {
                this.gdpBundleImages.push({
                'id': resp[i].id,
                'name': resp[i].title,
                'thumbnail_url': resp[i].thumb,
                'url': resp[i].preview
                });
            }
            if($state)
            {
                $state.loaded();
            }
          }
          else
          {
              this.loadedAllGdpBundleImages = true;   
              if($state)
              {
                  $state.complete();
              }
          }
          this.loading = false;
          this.loadingGdpBundleImages = false;
          this.gdpBundleImagesPage++;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        });
    },

    appendImages() {
      this.loadingImages = true;
      if( this.images.length > (this.imagesPage*this.imagesPerPage) ) {
        const self = this;

        setTimeout(() => {
          self.imagesPage++;
        },1000);

        setTimeout(() => {
          self.loadingImages = false;
        },2000);
      }
    },
    
    preConfirmCropping() {
      if( cookies.get('crop-message') ) this.$refs.cropper.saveit();
      else this.$refs.confirmSaveModal.open();
    },
    
    confirmCropping(saveCookie) {
      if( saveCookie ) cookies.set('crop-message', 1);
      this.$refs.cropper.saveit();
    },
    
    deleteImage({url}) {
      this.deleting = url;
      api.delete(`${window.gdEnv.VUE_APP_API_URL}/groovedigital/images/delete/${btoa(url)}`).then(() => {
        setTimeout(() => {
          this.deleting = null;
          this.selectedImage = null;
          this.openOptions = null;
          this.images = this.images.filter(
            (image) => image.url !== url
          );
        },1500);
      });
    },
  },

};
</script>
